import { useCallback, useEffect, useMemo, useRef, useState, type Dispatch, type ReactNode, type SetStateAction, type MouseEvent } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { CheckCircle2, Circle, CircleX, Loader2, Repeat, Trash2, CalendarDays, Flag, Tag, Clock, X, Copy, ListTree, Check, Star, StickyNote, Mail, Paperclip, ExternalLink, MoreVertical } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { TodoList, TodoProject, TodoTask, TodoTaskEmailLink } from '@/types/todo';
import { SwipeableRow, type SwipeActionConfig } from '@/features/email-list/components/SwipeableRow';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuLabel,
  ContextMenuSeparator,
  ContextMenuSub,
  ContextMenuSubContent,
  ContextMenuSubTrigger,
  ContextMenuTrigger,
} from '@/components/ui/context-menu';
import { useTodoActionsStore } from '@/stores/todoActionsStore';
import { useOperationStore } from '@/stores/operationStore';
import { useTodoModalStore } from '@/stores/todoModalStore';
import { useTodoStore } from '@/stores/todoStore';
import { useTodoViewStore } from '@/stores/todoViewStore';
import { MarkdownEditor } from './forms/MarkdownEditor';
import type { RepeatRuleState } from '@/types/repeat';
import { CalendarPopoverContent } from './forms/CalendarPopoverContent';
import { scheduleQuickOptions, dueQuickOptions } from '../utils/dateOptions';
import { formatDateOnly, parseDateOnly } from '../utils/dateHelpers';
import { fetchEmail, downloadAttachment } from '@/api/emails';
import type { Email } from '@/types/email';
import { useLinkedEmailViewerStore } from '@/stores/linkedEmailViewerStore';
import { LinkedClickUpPreview } from './LinkedClickUpPreview';
import { useIsMobile } from '@/hooks/useIsMobile';

interface TodoTaskCardProps {
  task: TodoTask;
  focused: boolean;
  multiSelected: boolean;
  onInteract: (taskId: string, mode: 'primary' | 'toggle' | 'range') => void;
  onComplete: (taskId: string) => void;
  onCancel: (taskId: string) => void;
  onDelete: (taskId: string) => void;
  showListContext: boolean;
  hideScheduledDate?: boolean;
  hideTags?: boolean;
  activeListId?: string | null;
  activeProjectId?: string | null;
  statusOverride?: StatusOverride;
  disableDrag?: boolean;
}
interface StatusOverride {
  icon: ReactNode;
  onClick?: (event: MouseEvent<HTMLButtonElement>) => void;
  ariaLabel?: string;
  disabled?: boolean;
}

const linkedEmailCache = new Map<string, Email>();

export function TodoTaskCard({
  task,
  focused,
  multiSelected,
  onInteract,
  onComplete,
  onCancel,
  onDelete,
  showListContext,
  hideScheduledDate = false,
  hideTags = false,
  activeListId,
  activeProjectId,
  statusOverride,
  disableDrag = false,
}: TodoTaskCardProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: `task-${task.id}`,
    disabled: disableDrag,
  });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 50 : undefined,
  };
  const swipeDisabled = task.status !== 'open';
  const { saveTask, setTaskStatus, toggleTaskSomeday, updateTaskTags, duplicateTask, purgeTask } = useTodoActionsStore();
  const operationState = useOperationStore((state) => state.getOperationState(task.id));
  const isOperationPending = operationState === 'pending';
  const hasOperationError = operationState === 'error';
  const [title, setTitle] = useState(task.title);
  const titleInputRef = useRef<HTMLInputElement>(null);
  const [notes, setNotes] = useState(task.notesMarkdown ?? '');

  // Track completion animation state - animate BEFORE the status actually changes
  const [isAnimatingComplete, setIsAnimatingComplete] = useState(false);
  const animationTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Cleanup animation timeout on unmount
  useEffect(() => {
    return () => {
      if (animationTimeoutRef.current) {
        clearTimeout(animationTimeoutRef.current);
      }
    };
  }, []);

  const handleCompleteWithAnimation = useCallback(() => {
    if (isAnimatingComplete) return;
    setIsAnimatingComplete(true);
    // Delay the actual completion to allow animation to play
    animationTimeoutRef.current = setTimeout(() => {
      onComplete(task.id);
    }, 900);
  }, [isAnimatingComplete, onComplete, task.id]);
  const hasPersistedNotes = Boolean((task.notesMarkdown ?? '').trim());
  const [schedule, setSchedule] = useState<string | null>(task.startDate ?? task.scheduledDate ?? null);
  const [dueDate, setDueDate] = useState<string | null>(task.dueDate ?? null);
  const [isSomeday, setSomeday] = useState(Boolean(task.isSomeday));
  const [linkedEmailEntries, setLinkedEmailEntries] = useState<Record<string, LinkedEmailEntry>>({});
  const [downloadingAttachments, setDownloadingAttachments] = useState<Record<string, boolean>>({});
  const openLinkedEmailViewer = useLinkedEmailViewerStore((state) => state.openViewer);
  const hasLinkedEmails = task.emails.length > 0;
  const emailLinksKey = useMemo(
    () => task.emails.map((link) => `${link.accountId}:${link.messageId}`).join('|'),
    [task.emails]
  );
  const clickupIntegration = task.integrations?.find((i) => i.integrationType === 'clickup');
  const hasClickUpIntegration = Boolean(clickupIntegration);
  const { unlinkClickUpIntegration } = useTodoActionsStore();
  const openTagManager = useTodoModalStore((state) => state.openTagManager);
  const setSelectedTaskId = useTodoViewStore((state) => state.setSelectedTaskId);

  // Handler to open tag manager - must set selectedTaskId first so the dialog knows which task
  const handleOpenTagManager = useCallback(() => {
    setSelectedTaskId(task.id);
    openTagManager();
  }, [task.id, setSelectedTaskId, openTagManager]);
  const [contextMenuOpen, setContextMenuOpen] = useState(false);
  const [swipeScheduleOpen, setSwipeScheduleOpen] = useState(false);
  const [swipeMoveOpen, setSwipeMoveOpen] = useState(false);
  const [swipeMenuOpen, setSwipeMenuOpen] = useState(false);
  // Detect touch device to disable context menu long-press
  const isTouchDevice = typeof window !== 'undefined' && ('ontouchstart' in window || navigator.maxTouchPoints > 0);
  const listOrder = useTodoStore((state) => state.listOrder);
  const listsById = useTodoStore((state) => state.lists);
  const projectsById = useTodoStore((state) => state.projects);
  const list = listsById[task.listId] ?? null;
  const project = task.projectId ? projectsById[task.projectId] ?? null : null;
  const availableLists = useMemo(
    () =>
      listOrder
        .map((id) => listsById[id])
        .filter((list): list is TodoList => Boolean(list)),
    [listOrder, listsById]
  );
  const projectsByListId = useMemo(() => {
    const grouped: Record<string, TodoProject[]> = {};
    Object.values(projectsById).forEach((project) => {
      if (!project || project.status !== 'active') {
        return;
      }
      const bucket = grouped[project.listId] ?? [];
      bucket.push(project);
      grouped[project.listId] = bucket;
    });
    Object.values(grouped).forEach((projects) => {
      projects.sort((a, b) => {
        if (a.sortKey !== b.sortKey) {
          return a.sortKey - b.sortKey;
        }
        return a.name.localeCompare(b.name);
      });
    });
    return grouped;
  }, [projectsById]);
  useEffect(() => {
    if (!focused || !hasLinkedEmails) {
      return;
    }
    let cancelled = false;
    task.emails.forEach((link) => {
      const key = `${link.accountId}:${link.messageId}`;
      const cached = linkedEmailCache.get(key);
      if (cached) {
        setLinkedEmailEntries((prev) => ({
          ...prev,
          [key]: { link, email: cached, loading: false, error: null },
        }));
        return;
      }
      setLinkedEmailEntries((prev) => ({
        ...prev,
        [key]: {
          link,
          email: prev[key]?.email ?? null,
          loading: true,
          error: null,
        },
      }));
      fetchEmail(link.accountId, link.messageId)
        .then((emailData) => {
          if (cancelled) {
            return;
          }
          linkedEmailCache.set(key, emailData);
          setLinkedEmailEntries((prev) => ({
            ...prev,
            [key]: { link, email: emailData, loading: false, error: null },
          }));
        })
        .catch((error: unknown) => {
          if (cancelled) {
            return;
          }
          setLinkedEmailEntries((prev) => ({
            ...prev,
            [key]: {
              link,
              email: null,
              loading: false,
              error: error instanceof Error ? error.message : 'Failed to load email',
            },
          }));
        });
    });
    return () => {
      cancelled = true;
    };
  }, [focused, hasLinkedEmails, emailLinksKey, task.emails]);

  const assignment = useMemo<AssignmentInfo | null>(() => {
    // Don't show assignment badge if we're viewing this specific project
    if (project && project.id === activeProjectId) {
      return null;
    }
    // Don't show assignment badge if we're viewing this specific list
    if (list && list.id === activeListId) {
      return null;
    }
    if (project) {
      return {
        type: 'project' as const,
        name: project.name,
        icon: project.icon ?? null,
        color: project.color ?? null,
      };
    }
    if (list && showListContext) {
      return {
        type: 'list' as const,
        name: list.name,
        icon: list.icon ?? null,
        color: list.color ?? null,
      };
    }
    return null;
  }, [list, project, showListContext, activeListId, activeProjectId]);

  // Get repeat state from task - check top-level repeat first, then metadata.repeat (for backwards compatibility)
  const getRepeatState = (): RepeatRuleState | null => {
    const metadataRepeat = task.metadata?.repeat;
    const repeat = task.repeat ?? (metadataRepeat as RepeatRuleState | undefined);
    if (!repeat || typeof repeat !== 'object') {
      return null;
    }
    if (repeat.enabled === false) {
      return null;
    }
    return repeat as RepeatRuleState;
  };

  useEffect(() => {
    setTitle(task.title);
    setNotes(task.notesMarkdown ?? '');
    setSchedule(task.startDate ?? task.scheduledDate ?? null);
    setDueDate(task.dueDate ?? null);
    setSomeday(Boolean(task.isSomeday));
  }, [task]);

  // Track if this is a newly created task (never had content)
  const isNewEmptyTask = !task.title;

  // Focus title input when task becomes focused AND it's a new task
  useEffect(() => {
    if (focused && isNewEmptyTask && titleInputRef.current) {
      titleInputRef.current.focus();
    }
  }, [focused, isNewEmptyTask]);

  const handleSave = useCallback(
    async (updates: Partial<TodoTask>) => {
      await saveTask(task.id, updates);
    },
    [saveTask, task.id]
  );

  // Handle Escape key to close the task (and delete if empty)
  useEffect(() => {
    if (!focused) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        const trimmedTitle = title.trim();
        const trimmedNotes = notes.trim();
        // If this is a new empty task, delete it
        if (!task.title && !trimmedTitle && !trimmedNotes) {
          void purgeTask(task.id);
        } else if (title !== task.title) {
          void handleSave({ title });
        }
        // Close the task
        onInteract(task.id, 'primary');
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [focused, title, notes, task.title, task.id, purgeTask, handleSave, onInteract]);

  const handleScheduleSubmit = useCallback(
    (value: string | null) => {
      setSchedule(value);
      void handleSave({
        startDate: value,
        scheduledDate: value,
        ...(value ? { isSomeday: false } : {}),
      });
    },
    [handleSave]
  );

  const handleScheduleClear = useCallback(() => {
    setSchedule(null);
    void handleSave({ startDate: null, scheduledDate: null });
  }, [handleSave]);

  const handleDueDateSubmit = useCallback(
    (value: string | null) => {
      setDueDate(value);
      void handleSave({
        dueDate: value,
        ...(value ? { isSomeday: false } : {}),
      });
    },
    [handleSave]
  );

  const handleDueDateClear = useCallback(() => {
    setDueDate(null);
    void handleSave({ dueDate: null });
  }, [handleSave]);

  const handleRepeatSubmit = useCallback(
    (state: RepeatRuleState) => {
      void handleSave({ repeat: state });
    },
    [handleSave]
  );

  const handleRepeatClear = useCallback(() => {
    void handleSave({ repeat: null });
  }, [handleSave]);

  const handleDuplicateTask = useCallback(() => {
    void duplicateTask(task.id);
  }, [duplicateTask, task.id]);

  const handleToggleSomeday = useCallback(() => {
    const next = !isSomeday;
    setSomeday(next);
    void toggleTaskSomeday(task.id, next);
  }, [isSomeday, task.id, toggleTaskSomeday]);

  const handleMoveDestination = useCallback(
    ({ listId, projectId }: { listId: string; projectId: string | null }) => {
      if (listId === task.listId && (projectId ?? null) === (task.projectId ?? null)) {
        return;
      }
      void handleSave({ listId, projectId });
    },
    [handleSave, task.listId, task.projectId]
  );

  const handleAttachmentDownload = useCallback(
    async (emailTarget: Email, attachmentId: string, filename?: string | null) => {
      const key = `${emailTarget.id}:${attachmentId}`;
      setDownloadingAttachments((prev) => ({ ...prev, [key]: true }));
      try {
        const { blob, filename: serverFilename } = await downloadAttachment(
          emailTarget.accountId,
          emailTarget.id,
          attachmentId,
          emailTarget.folderId,
        );
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = serverFilename || filename || 'attachment';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } catch (error) {
        console.error('Failed to download attachment', error);
      } finally {
        setDownloadingAttachments((prev) => {
          const next = { ...prev };
          delete next[key];
          return next;
        });
      }
    },
    []
  );

  const handleReopen = useCallback(() => {
    void setTaskStatus(task.id, 'open');
  }, [setTaskStatus, task.id]);

  const closeContextMenu = useCallback(() => setContextMenuOpen(false), []);

  const repeatState = getRepeatState();

  const handleNotesBlur = useCallback(
    (value: string) => {
      const normalizedValue = (value || '').trim();
      const normalizedCurrent = (task.notesMarkdown || '').trim();
      if (normalizedValue === normalizedCurrent) {
        return;
      }
      void handleSave({ notesMarkdown: value });
    },
    [handleSave, task.notesMarkdown]
  );

  const statusIcon = useMemo(() => {
    if (task.status === 'completed' || isAnimatingComplete) {
      return (
        <span className={cn('inline-flex', isAnimatingComplete && 'animate-task-complete')}>
          <CheckCircle2 className="h-5 w-5 text-success-ink" />
        </span>
      );
    }
    if (task.status === 'cancelled') {
      return <CircleX className="h-5 w-5 text-warning-ink" />;
    }
    return <Circle className="h-5 w-5 text-muted-foreground" />;
  }, [task.status, isAnimatingComplete]);
  const effectiveStatusIcon = statusOverride?.icon ?? statusIcon;
  const statusButtonAriaLabel =
    statusOverride?.ariaLabel ??
    (task.status === 'completed' ? 'Mark task as open' : 'Mark task as completed');

  const handleStatusButtonClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();
      if (statusOverride) {
        statusOverride.onClick?.(event);
        return;
      }
      if (task.status === 'completed') {
        onCancel(task.id);
      } else {
        handleCompleteWithAnimation();
      }
    },
    [onCancel, handleCompleteWithAnimation, statusOverride, task.id, task.status]
  );

  const openScheduleDialog = useCallback(() => setSwipeScheduleOpen(true), []);
  const openMoveDialog = useCallback(() => setSwipeMoveOpen(true), []);
  const openMenuDialog = useCallback(() => setSwipeMenuOpen(true), []);

  const leftActions = useMemo<SwipeActionConfig[]>(() => {
    if (swipeDisabled || isAnimatingComplete) {
      return [];
    }
    return [
      {
        key: 'schedule',
        label: 'Schedule',
        icon: <CalendarDays className="h-5 w-5" />,
        tone: 'primary',
        onAction: openScheduleDialog,
      },
    ];
  }, [swipeDisabled, isAnimatingComplete, openScheduleDialog]);

  const rightActions = useMemo<SwipeActionConfig[]>(() => {
    if (swipeDisabled || isAnimatingComplete) {
      return [];
    }
    return [
      {
        key: 'move',
        label: 'Move',
        icon: <ListTree className="h-5 w-5" />,
        tone: 'primary',
        onAction: openMoveDialog,
      },
      {
        key: 'more',
        label: 'More',
        icon: <MoreVertical className="h-5 w-5" />,
        tone: 'muted',
        onAction: openMenuDialog,
      },
    ];
  }, [swipeDisabled, isAnimatingComplete, openMoveDialog, openMenuDialog]);

  return (
    <div ref={setNodeRef} style={style} className="w-full max-w-4xl mx-auto">
      <ContextMenu onOpenChange={setContextMenuOpen}>
        <ContextMenuTrigger asChild disabled={isTouchDevice}>
          <div className="w-full max-w-4xl mx-auto">
            <SwipeableRow
              className="w-full"
              contentClassName="w-full rounded-2xl"
              leftActions={leftActions}
              rightActions={rightActions}
              disabled={swipeDisabled}
            >
              <div
                data-task-id={task.id}
                className={cn(
                  'group relative rounded-2xl border border-border/60 bg-card transition-all duration-200 shadow-sm',
                  focused
                    ? 'border-primary shadow-primary/30'
                    : multiSelected
                      ? 'border-primary/60 ring-1 ring-primary/40'
                      : 'hover:border-primary/40',
                  isDragging && 'border-transparent ring-2 ring-primary/40 shadow-2xl',
                  isAnimatingComplete && 'animate-task-card-complete'
                )}
              >
          {isOperationPending || hasOperationError ? (
            <div className="absolute right-3 top-3 flex items-center gap-2 text-xs">
              {isOperationPending ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin text-primary" />
              ) : null}
              {hasOperationError ? (
                <span className="font-medium text-destructive">Error</span>
              ) : null}
            </div>
          ) : null}
          <div
            className={cn('p-3 sm:p-4', focused && 'pb-2')}
            {...attributes}
            {...listeners}
            role="button"
            tabIndex={0}
            onClick={(event) => {
              // Don't handle clicks if they're inside an input or contenteditable element
              const target = event.target as HTMLElement;
              const isInInput = target.closest('input, textarea, [contenteditable="true"]');
              if (isInInput) {
                return;
              }

              // When task is already focused/expanded, don't close it on internal clicks.
              // Task is closed via Escape, Enter on title, or clicking a different task.
              if (focused && !event.shiftKey && !event.metaKey && !event.ctrlKey) {
                return;
              }

              if (event.shiftKey) {
                onInteract(task.id, 'range');
              } else if (event.metaKey || event.ctrlKey) {
                onInteract(task.id, 'toggle');
              } else {
                onInteract(task.id, 'primary');
              }
            }}
            onKeyDown={(event) => {
              // Don't handle Enter if typing in an input or contenteditable element
              const target = event.target as HTMLElement;
              const isInInput = target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable;
              if (isInInput) {
                return;
              }
              
              if (event.key === 'Enter') {
                onInteract(task.id, 'primary');
              }
            }}
          >
            <div className={cn('flex gap-3 min-w-0', focused ? 'items-start' : 'items-center')}>
              <ContextMenu>
                <ContextMenuTrigger asChild>
                  <button
                    type="button"
                    className="rounded-full p-1 transition-colors hover:bg-primary/10"
                    onClick={handleStatusButtonClick}
                    onContextMenu={(event) => {
                      event.stopPropagation();
                    }}
                    aria-label={statusButtonAriaLabel}
                    disabled={statusOverride?.disabled || isAnimatingComplete}
                  >
                    {effectiveStatusIcon}
                  </button>
                </ContextMenuTrigger>
                <ContextMenuContent>
                  <ContextMenuItem
                    onClick={(event) => {
                      event.stopPropagation();
                      handleCompleteWithAnimation();
                    }}
                  >
                    <CheckCircle2 className="h-5 w-5 mr-2" />
                    Complete
                  </ContextMenuItem>
                  <ContextMenuItem
                    onClick={(event) => {
                      event.stopPropagation();
                      onCancel(task.id);
                    }}
                  >
                    <CircleX className="h-5 w-5 mr-2" />
                    Cancel
                  </ContextMenuItem>
                  <ContextMenuItem
                    variant="destructive"
                    onClick={(event) => {
                      event.stopPropagation();
                      onDelete(task.id);
                    }}
                  >
                    <Trash2 className="h-5 w-5 mr-2" />
                    Delete
                  </ContextMenuItem>
                </ContextMenuContent>
              </ContextMenu>
              <div className="flex-1 min-w-0 overflow-hidden">
                {focused ? (
                  <div className="space-y-3">
                    {assignment ? (
                      <TaskAssignmentBadge {...assignment} />
                    ) : null}
                    <Input
                      ref={titleInputRef}
                      value={title}
                      onChange={(event) => setTitle(event.target.value)}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter') {
                          event.preventDefault();
                          if (title !== task.title) {
                            void handleSave({ title });
                          }
                          onInteract(task.id, 'primary');
                        } else if (event.key === 'Escape') {
                          event.preventDefault();
                          const trimmedTitle = title.trim();
                          const trimmedNotes = notes.trim();
                          // If this is a new empty task, delete it
                          if (!task.title && !trimmedTitle && !trimmedNotes) {
                            void purgeTask(task.id);
                          } else if (title !== task.title) {
                            void handleSave({ title });
                          }
                          // Close the task
                          onInteract(task.id, 'primary');
                        }
                      }}
                      onBlur={() => {
                        const trimmedTitle = title.trim();
                        const trimmedNotes = notes.trim();
                        // If this is a new empty task (never saved with content), delete it
                        if (!task.title && !trimmedTitle && !trimmedNotes) {
                          void purgeTask(task.id);
                          return;
                        }
                        if (title !== task.title) {
                          void handleSave({ title });
                        }
                      }}
                      onClick={(e) => e.stopPropagation()}
                      className="w-full text-sm font-medium border-none shadow-none px-0 h-auto focus-visible:ring-0 -mt-0.5"
                      placeholder="Task title"
                    />
                    <MarkdownEditor
                      value={notes}
                      onChange={setNotes}
                      onBlur={(latestValue) => {
                        if (latestValue !== notes) {
                          setNotes(latestValue);
                        }
                        handleNotesBlur(latestValue);
                      }}
                      placeholder="Notes"
                    />
                    {/* Tags - always show in expanded view for editing */}
                    {task.tags.length > 0 && (
                      <div className="flex items-center gap-2">
                        <div className="flex flex-wrap items-center gap-2">
                          {task.tags.map((tag) => (
                            <Badge
                              key={tag.id}
                              style={{ backgroundColor: tag.color ? `${tag.color}33` : undefined, color: tag.color ?? undefined }}
                              className="group/tag relative cursor-pointer"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleOpenTagManager();
                              }}
                            >
                              {tag.name}
                              <button
                                type="button"
                                className="rounded-full p-0.5 transition-opacity"
                                style={{
                                  transition: "background 0.1s",
                                }}
                                onMouseEnter={e => {
                                  if (tag.color) {
                                    (e.currentTarget as HTMLButtonElement).style.background = tag.color + "33";
                                  }
                                }}
                                onMouseLeave={e => {
                                  (e.currentTarget as HTMLButtonElement).style.background = "";
                                }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  const remainingTagIds = task.tags.filter((t) => t.id !== tag.id).map((t) => t.id);
                                  void updateTaskTags(task.id, remainingTagIds);
                                }}
                              >
                                <X className="h-3 w-3" />
                              </button>
                            </Badge>
                          ))}
                          <button
                            type="button"
                            className="text-xs text-muted-foreground hover:text-foreground"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleOpenTagManager();
                            }}
                          >
                            +
                          </button>
                        </div>
                        {/* Icon buttons on same line as tags if tags is the last expanded field */}
                        {!schedule && !dueDate && !getRepeatState()?.enabled && (
                          <div className="flex items-center gap-1.5 ml-auto">
                            {!schedule && (
                              <IconDateFieldTrigger
                                icon={<CalendarDays className="h-4 w-4 text-muted-foreground hover:text-foreground" />}
                                label="Scheduled"
                                date={null}
                                onSubmit={(value) => {
                                  setSchedule(value);
                                  void handleSave({ startDate: value, scheduledDate: value, isSomeday: false });
                                }}
                                onClear={() => {}}
                                quickOptions={scheduleQuickOptions}
                              />
                            )}
                            {!dueDate && (
                              <IconDateFieldTrigger
                                icon={<Flag className="h-4 w-4 text-muted-foreground hover:text-foreground" />}
                                label="Deadline"
                                date={null}
                                onSubmit={(value) => {
                                  setDueDate(value);
                                  void handleSave({ dueDate: value, isSomeday: false });
                                }}
                                onClear={() => {}}
                                quickOptions={dueQuickOptions}
                              />
                            )}
                            {(!getRepeatState() || !getRepeatState()?.enabled) && (
                              <IconRepeatFieldTrigger
                                icon={<Repeat className="h-4 w-4 text-muted-foreground hover:text-foreground" />}
                                label="Repeat"
                                repeatState={null}
                            onSubmit={(state) => {
                              // Save repeat as top-level field (API expects it this way)
                              void handleSave({ repeat: state });
                            }}
                            onClear={() => {
                              // Clear repeat by setting it to null
                              void handleSave({ repeat: null });
                            }}
                              />
                            )}
                          </div>
                        )}
                      </div>
                    )}
                    {hasLinkedEmails && (
                      <div className="space-y-3">
                        {task.emails.map((link) => {
                          const key = `${link.accountId}:${link.messageId}`;
                          const entry =
                            linkedEmailEntries[key] ??
                            {
                              link,
                              email: null,
                              loading: true,
                              error: null,
                            };
                          return (
                            <LinkedEmailPreview
                              key={key}
                              entry={entry}
                              downloadingMap={downloadingAttachments}
                              onViewEmail={() => {
                                if (entry.email) {
                                  openLinkedEmailViewer(entry.email);
                                }
                              }}
                              onDownloadAttachment={(attachmentId, filename) => {
                                if (entry.email) {
                                  void handleAttachmentDownload(entry.email, attachmentId, filename);
                                }
                              }}
                            />
                          );
                        })}
                      </div>
                    )}
                    {/* ClickUp integration */}
                    {clickupIntegration && (
                      <div className="space-y-2">
                        <LinkedClickUpPreview
                          integration={clickupIntegration}
                          onOpenClickUp={() => {
                            if (clickupIntegration.externalUrl) {
                              window.open(clickupIntegration.externalUrl, '_blank');
                            }
                          }}
                          onUnlink={() => {
                            void unlinkClickUpIntegration(task.id);
                          }}
                        />
                      </div>
                    )}
                    {/* Schedule date - only show if scheduled */}
                    {schedule && (
                      <div className="flex items-center gap-2">
                        <EditableDateField
                          icon={<CalendarDays className="h-4 w-4 text-primary" />}
                          label="Scheduled"
                          date={schedule}
                          onSubmit={(value) => {
                            setSchedule(value);
                            void handleSave({ startDate: value, scheduledDate: value, isSomeday: false });
                          }}
                          onClear={() => {
                            setSchedule(null);
                            void handleSave({ startDate: null, scheduledDate: null });
                          }}
                          quickOptions={scheduleQuickOptions}
                        />
                        {/* Icon buttons on same line as scheduled if scheduled is the last expanded field */}
                        {!dueDate && !getRepeatState()?.enabled && (
                          <div className="flex items-center gap-1.5 ml-auto">
                            {task.tags.length === 0 && (
                              <IconPopoverTrigger
                                icon={<Tag className="h-4 w-4 text-muted-foreground hover:text-foreground" />}
                                onOpen={handleOpenTagManager}
                              />
                            )}
                            {!dueDate && (
                              <IconDateFieldTrigger
                                icon={<Flag className="h-4 w-4 text-muted-foreground hover:text-foreground" />}
                                label="Deadline"
                                date={null}
                                onSubmit={(value) => {
                                  setDueDate(value);
                                  void handleSave({ dueDate: value, isSomeday: false });
                                }}
                                onClear={() => {}}
                                quickOptions={dueQuickOptions}
                              />
                            )}
                            {(!getRepeatState() || !getRepeatState()?.enabled) && (
                              <IconRepeatFieldTrigger
                                icon={<Repeat className="h-4 w-4 text-muted-foreground hover:text-foreground" />}
                                label="Repeat"
                                repeatState={null}
                            onSubmit={(state) => {
                              // Save repeat as top-level field (API expects it this way)
                              void handleSave({ repeat: state });
                            }}
                            onClear={() => {
                              // Clear repeat by setting it to null
                              void handleSave({ repeat: null });
                            }}
                              />
                            )}
                          </div>
                        )}
                      </div>
                    )}
                    {/* Due date - only show if due date exists */}
                    {dueDate && (
                      <div className="flex items-center gap-2">
                        <EditableDateField
                          icon={<Flag className="h-4 w-4 text-primary" />}
                          label="Deadline"
                          date={dueDate}
                          onSubmit={(value) => {
                            setDueDate(value);
                            void handleSave({ dueDate: value, isSomeday: false });
                          }}
                          onClear={() => {
                            setDueDate(null);
                            void handleSave({ dueDate: null });
                          }}
                          quickOptions={dueQuickOptions}
                        />
                        {/* Icon buttons on same line as deadline if deadline is the last expanded field */}
                        {!getRepeatState()?.enabled && (
                          <div className="flex items-center gap-1.5 ml-auto">
                            {task.tags.length === 0 && (
                              <IconPopoverTrigger
                                icon={<Tag className="h-4 w-4 text-muted-foreground hover:text-foreground" />}
                                onOpen={handleOpenTagManager}
                              />
                            )}
                            {!schedule && (
                              <IconDateFieldTrigger
                                icon={<CalendarDays className="h-4 w-4 text-muted-foreground hover:text-foreground" />}
                                label="Scheduled"
                                date={null}
                                onSubmit={(value) => {
                                  setSchedule(value);
                                  void handleSave({ startDate: value, scheduledDate: value, isSomeday: false });
                                }}
                                onClear={() => {}}
                                quickOptions={scheduleQuickOptions}
                              />
                            )}
                            {(!getRepeatState() || !getRepeatState()?.enabled) && (
                              <IconRepeatFieldTrigger
                                icon={<Repeat className="h-4 w-4 text-muted-foreground hover:text-foreground" />}
                                label="Repeat"
                                repeatState={null}
                            onSubmit={(state) => {
                              // Save repeat as top-level field (API expects it this way)
                              void handleSave({ repeat: state });
                            }}
                            onClear={() => {
                              // Clear repeat by setting it to null
                              void handleSave({ repeat: null });
                            }}
                              />
                            )}
                          </div>
                        )}
                      </div>
                    )}
                    {/* Repeat field - only show if repeat is enabled */}
                    {getRepeatState()?.enabled && (
                      <div className="flex items-center gap-2">
                        <EditableRepeatField
                          icon={<Repeat className="h-4 w-4 text-primary" />}
                          label="Repeat"
                          repeatState={getRepeatState()}
                      onSubmit={(state) => {
                        // Save repeat as top-level field (API expects it this way)
                        void handleSave({ repeat: state });
                      }}
                      onClear={() => {
                        // Clear repeat by setting it to null
                        void handleSave({ repeat: null });
                      }}
                        />
                        {/* Icon buttons on same line as repeat (always the last expanded field) */}
                        <div className="flex items-center gap-1.5 ml-auto">
                          {task.tags.length === 0 && (
                            <IconPopoverTrigger
                              icon={<Tag className="h-4 w-4 text-muted-foreground hover:text-foreground" />}
                              onOpen={handleOpenTagManager}
                            />
                          )}
                          {!schedule && (
                            <IconDateFieldTrigger
                              icon={<CalendarDays className="h-4 w-4 text-muted-foreground hover:text-foreground" />}
                              label="Scheduled"
                              date={null}
                              onSubmit={(value) => {
                                setSchedule(value);
                                void handleSave({ startDate: value, scheduledDate: value, isSomeday: false });
                              }}
                              onClear={() => {}}
                              quickOptions={scheduleQuickOptions}
                            />
                          )}
                          {!dueDate && (
                            <IconDateFieldTrigger
                              icon={<Flag className="h-4 w-4 text-muted-foreground hover:text-foreground" />}
                              label="Deadline"
                              date={null}
                              onSubmit={(value) => {
                                setDueDate(value);
                                void handleSave({ dueDate: value, isSomeday: false });
                              }}
                              onClear={() => {}}
                              quickOptions={dueQuickOptions}
                            />
                          )}
                        </div>
                      </div>
                    )}
                    {/* Icon buttons on their own line if no expanded fields exist */}
                    {task.tags.length === 0 && !schedule && !dueDate && !getRepeatState()?.enabled && (
                      <div className="flex items-center gap-1.5">
                        <IconPopoverTrigger
                          icon={<Tag className="h-4 w-4 text-muted-foreground hover:text-foreground" />}
                          onOpen={handleOpenTagManager}
                        />
                        <IconDateFieldTrigger
                          icon={<CalendarDays className="h-4 w-4 text-muted-foreground hover:text-foreground" />}
                          label="Scheduled"
                          date={null}
                          onSubmit={(value) => {
                            setSchedule(value);
                            void handleSave({ startDate: value, scheduledDate: value, isSomeday: false });
                          }}
                          onClear={() => {}}
                          quickOptions={scheduleQuickOptions}
                        />
                        <IconDateFieldTrigger
                          icon={<Flag className="h-4 w-4 text-muted-foreground hover:text-foreground" />}
                          label="Deadline"
                          date={null}
                          onSubmit={(value) => {
                            setDueDate(value);
                            void handleSave({ dueDate: value, isSomeday: false });
                          }}
                          onClear={() => {}}
                          quickOptions={dueQuickOptions}
                        />
                        <IconRepeatFieldTrigger
                          icon={<Repeat className="h-4 w-4 text-muted-foreground hover:text-foreground" />}
                          label="Repeat"
                          repeatState={null}
                            onSubmit={(state) => {
                              // Save repeat as top-level field (API expects it this way)
                              void handleSave({ repeat: state });
                            }}
                            onClear={() => {
                              // Clear repeat by setting it to null
                              void handleSave({ repeat: null });
                            }}
                        />
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="flex flex-col gap-1.5">
                    <div className="flex items-center gap-2 flex-wrap">
                      {/* Optional schedule date label - only show if not grouped by date */}
                      {!hideScheduledDate && schedule && (() => {
                        const today = new Date();
                        const todayOnly = formatDateOnly(today);
                        const scheduleOnly = schedule.slice(0, 10);
                        const isScheduledToday = scheduleOnly === todayOnly;
                        return (
                          <>
                            {isScheduledToday ? (
                              <Star className="h-4 w-4 text-[#ffd400] fill-[#ffd400]" />
                            ) : (
                              <Badge variant="secondary" >
                                {formatShortDate(schedule)}
                              </Badge>
                            )}
                          </>
                        );
                      })()}
                      {/* Task title */}
                      <div className="flex min-w-0 flex-1 items-center gap-1">
                        <span className={cn(
                          'truncate text-sm font-medium',
                          (task.status === 'completed' || isAnimatingComplete) && 'text-muted-foreground',
                          task.status === 'completed' && 'line-through',
                          isAnimatingComplete && 'animate-task-strikethrough'
                        )}>
                          {task.title}
                        </span>
                        {hasPersistedNotes ? (
                          <StickyNote className="h-3.5 w-3.5 text-muted-foreground" />
                        ) : null}
                        {hasLinkedEmails ? <Mail className="h-3.5 w-3.5 text-muted-foreground" /> : null}
                        {hasClickUpIntegration ? <ExternalLink className="h-3.5 w-3.5 text-muted-foreground" /> : null}
                      </div>
                      {/* Tags - hidden when grouped by tag */}
                      {!hideTags && task.tags.map((tag) => (
                        <Badge
                          key={tag.id}
                          className="text-xs px-2 py-0"
                          style={{ backgroundColor: tag.color ? `${tag.color}33` : undefined, color: tag.color ?? undefined }}
                        >
                          {tag.name}
                        </Badge>
                      ))}
                      {/* Deadline */}
                      {dueDate && (() => {
                        const today = new Date();
                        const todayOnly = formatDateOnly(today);
                        const dueDateOnly = dueDate.slice(0, 10);
                        const isDueToday = dueDateOnly === todayOnly;
                        const isOverdue = dueDateOnly < todayOnly;
                        const highlight = isDueToday || isOverdue;
                        return (
                          <span
                            className={cn(
                              'flex items-center gap-1 text-xs',
                              highlight ? 'text-destructive font-semibold' : 'text-muted-foreground'
                            )}
                          >
                            <Flag className="h-3 w-3" />
                            {formatDueDateDisplay(dueDate)}
                          </span>
                        );
                      })()}
                      {/* Repeat indicator */}
                      {task.recurrenceId && (
                        <Repeat className="h-3 w-3 text-muted-foreground" />
                      )}
                    </div>
                    {/* Project/List assignment - shown below task name */}
                    {assignment && (
                      <TaskAssignmentBadge {...assignment} />
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
          {/* Hover action bar removed in favor of the context menu */}
              </div>
            </SwipeableRow>
          </div>
        </ContextMenuTrigger>
        <TaskContextMenuContent
          task={task}
          schedule={schedule}
          dueDate={dueDate}
          repeatState={repeatState}
          lists={availableLists}
          projectsByListId={projectsByListId}
          projectsLookup={projectsById}
          menuOpen={contextMenuOpen}
          onScheduleChange={handleScheduleSubmit}
          onScheduleClear={handleScheduleClear}
          onDueDateChange={handleDueDateSubmit}
          onDueDateClear={handleDueDateClear}
          onMove={handleMoveDestination}
          onOpenTags={handleOpenTagManager}
          onRepeatSubmit={handleRepeatSubmit}
          onRepeatClear={handleRepeatClear}
          onDuplicate={handleDuplicateTask}
          onComplete={handleCompleteWithAnimation}
          onCancel={() => onCancel(task.id)}
          onReopen={handleReopen}
          onDelete={() => onDelete(task.id)}
          onToggleSomeday={handleToggleSomeday}
          closeMenu={closeContextMenu}
        />
      </ContextMenu>

      {/* Swipe-triggered Schedule Dialog */}
      <Dialog open={swipeScheduleOpen} onOpenChange={setSwipeScheduleOpen}>
        <DialogContent className="w-auto max-w-[95vw] p-0 border-none bg-transparent shadow-none">
          <DialogTitle className="sr-only">Schedule Task</DialogTitle>
          <CalendarPopoverContent
            selected={parseDateOnly(schedule) ?? undefined}
            onSelect={(day) => {
              if (!day) return;
              handleScheduleSubmit(formatDateOnly(day));
              setSwipeScheduleOpen(false);
            }}
            quickOptions={scheduleQuickOptions}
            onQuickSelect={(getDate) => {
              handleScheduleSubmit(formatDateOnly(getDate()));
              setSwipeScheduleOpen(false);
            }}
            onClear={() => {
              handleScheduleClear();
              setSwipeScheduleOpen(false);
            }}
            onClose={() => setSwipeScheduleOpen(false)}
          />
        </DialogContent>
      </Dialog>

      {/* Swipe-triggered Move Dialog */}
      <Dialog open={swipeMoveOpen} onOpenChange={setSwipeMoveOpen}>
        <DialogContent className="w-80 max-w-[95vw] p-0">
          <DialogTitle className="px-4 pt-4 pb-2 text-sm font-semibold">Move to...</DialogTitle>
          <MoveDestinationMenu
            lists={availableLists}
            projectsByListId={projectsByListId}
            activeListId={task.listId}
            activeProjectId={task.projectId}
            onSelect={(target) => {
              handleMoveDestination(target);
              setSwipeMoveOpen(false);
            }}
          />
        </DialogContent>
      </Dialog>

      {/* Swipe-triggered Context Menu Dialog */}
      <Dialog open={swipeMenuOpen} onOpenChange={setSwipeMenuOpen}>
        <DialogContent className="w-80 max-w-[95vw] p-2">
          <DialogTitle className="sr-only">Task Actions</DialogTitle>
          <SwipeContextMenuContent
            task={task}
            schedule={schedule}
            dueDate={dueDate}
            repeatState={repeatState}
            lists={availableLists}
            projectsByListId={projectsByListId}
            projectsLookup={projectsById}
            onScheduleChange={handleScheduleSubmit}
            onScheduleClear={handleScheduleClear}
            onDueDateChange={handleDueDateSubmit}
            onDueDateClear={handleDueDateClear}
            onMove={handleMoveDestination}
            onOpenTags={handleOpenTagManager}
            onRepeatSubmit={handleRepeatSubmit}
            onRepeatClear={handleRepeatClear}
            onDuplicate={handleDuplicateTask}
            onComplete={handleCompleteWithAnimation}
            onCancel={() => onCancel(task.id)}
            onReopen={handleReopen}
            onDelete={() => onDelete(task.id)}
            onToggleSomeday={handleToggleSomeday}
            closeMenu={() => setSwipeMenuOpen(false)}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}

export interface AssignmentInfo {
  type: 'list' | 'project';
  name: string;
  icon?: string | null;
  color?: string | null;
}

interface LinkedEmailEntry {
  link: TodoTaskEmailLink;
  email: Email | null;
  loading: boolean;
  error: string | null;
}

interface LinkedEmailPreviewProps {
  entry: LinkedEmailEntry;
  downloadingMap: Record<string, boolean>;
  onViewEmail: () => void;
  onDownloadAttachment: (attachmentId: string, filename?: string | null) => void;
}

function LinkedEmailPreview({
  entry,
  downloadingMap,
  onViewEmail,
  onDownloadAttachment,
}: LinkedEmailPreviewProps) {
  if (entry.loading) {
    return (
      <div className="rounded-xl border border-border/40 bg-background/60 p-3 text-xs text-muted-foreground">
        <Loader2 className="mr-2 inline h-3 w-3 animate-spin" />
        Loading linked email…
      </div>
    );
  }
  if (entry.error) {
    return (
      <div className="rounded-xl border border-destructive/20 bg-destructive/5 p-3 text-xs text-destructive">
        {entry.error}
      </div>
    );
  }
  if (!entry.email) {
    return null;
  }
  const email = entry.email;
  const attachments = email.attachments ?? [];
  return (
    <div className="rounded-2xl border border-border/60 bg-muted/40 p-3 space-y-3" onClick={(e) => e.stopPropagation()}>
      <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between sm:gap-3">
        <div className="space-y-0.5 min-w-0">
          <p className="text-sm font-semibold truncate">{email.subject || '(no subject)'}</p>
          <p className="text-xs text-muted-foreground truncate">{email.from.name || email.from.email}</p>
          <p className="text-xs text-muted-foreground">{new Date(email.date).toLocaleString()}</p>
        </div>
        <Button variant="secondary" size="sm" className="gap-2 self-start shrink-0" onClick={onViewEmail}>
          <ExternalLink className="h-3.5 w-3.5" />
          View
        </Button>
      </div>
      {email.snippet ? <p className="text-sm text-muted-foreground">{email.snippet}</p> : null}
      {attachments.length > 0 && (
        <div className="space-y-1">
          <p className="text-xs font-semibold text-muted-foreground">Attachments</p>
          <div className="flex flex-wrap gap-2">
            {attachments.map((attachment) => {
              const downloadKey = `${email.id}:${attachment.id}`;
              const downloading = Boolean(downloadingMap[downloadKey]);
              return (
                <Button
                  key={attachment.id}
                  variant="outline"
                  size="sm"
                  className="gap-2 text-xs"
                  disabled={downloading}
                  onClick={() => onDownloadAttachment(attachment.id, attachment.filename)}
                >
                  {downloading ? (
                    <Loader2 className="h-3 w-3 animate-spin" />
                  ) : (
                    <Paperclip className="h-3 w-3" />
                  )}
                  <span>{attachment.filename || 'Attachment'}</span>
                </Button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

export function TaskAssignmentBadge({ name, icon, color }: AssignmentInfo) {
  const accentStyles = color
    ? {
        backgroundColor: colorToRgba(color, 0.15),
        color: color,
      }
    : undefined;

  return (
    <Badge
      variant={color ? undefined : 'secondary'}
      style={accentStyles}
    >
      {icon ? <span>{icon}</span> : null}
      <span>{name}</span>
    </Badge>
  );
}

function colorToRgba(color?: string | null, alpha = 0.12) {
  if (!color || typeof color !== 'string') {
    return undefined;
  }
  if (!color.startsWith('#')) {
    return color;
  }
  const hex = color.slice(1);
  const normalized = hex.length === 3 ? hex.split('').map((char) => char + char).join('') : hex.slice(0, 6);
  if (!/^[0-9a-fA-F]{6}$/.test(normalized)) {
    return undefined;
  }
  const value = parseInt(normalized, 16);
  const r = (value >> 16) & 255;
  const g = (value >> 8) & 255;
  const b = value & 255;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

interface TaskContextMenuContentProps {
  task: TodoTask;
  schedule: string | null;
  dueDate: string | null;
  repeatState: RepeatRuleState | null;
  lists: TodoList[];
  projectsByListId: Record<string, TodoProject[]>;
  projectsLookup: Record<string, TodoProject>;
  menuOpen: boolean;
  onScheduleChange: (value: string | null) => void;
  onScheduleClear: () => void;
  onDueDateChange: (value: string | null) => void;
  onDueDateClear: () => void;
  onMove: (destination: { listId: string; projectId: string | null }) => void;
  onOpenTags: () => void;
  onRepeatSubmit: (state: RepeatRuleState) => void;
  onRepeatClear: () => void;
  onDuplicate: () => void;
  onComplete: () => void;
  onCancel: () => void;
  onReopen: () => void;
  onDelete: () => void;
  onToggleSomeday: () => void;
  closeMenu: () => void;
}

function TaskContextMenuContent({
  task,
  schedule,
  dueDate,
  repeatState,
  lists,
  projectsByListId,
  projectsLookup,
  menuOpen,
  onScheduleChange,
  onScheduleClear,
  onDueDateChange,
  onDueDateClear,
  onMove,
  onOpenTags,
  onRepeatSubmit,
  onRepeatClear,
  onDuplicate,
  onComplete,
  onCancel,
  onReopen,
  onDelete,
  onToggleSomeday,
  closeMenu,
}: TaskContextMenuContentProps) {
  return (
    <ContextMenuContent className="w-72 sm:w-80 space-y-1">
      <ContextMenuLabel className="line-clamp-2 text-sm font-semibold">
        {task.title || 'Untitled Task'}
      </ContextMenuLabel>
      <TaskDateMenuSub
        label="Schedule"
        icon={<CalendarDays className="h-4 w-4" />}
        date={schedule}
        quickOptions={scheduleQuickOptions}
        onSubmit={onScheduleChange}
        onClear={onScheduleClear}
        closeMenu={closeMenu}
      />
      <TaskDateMenuSub
        label="Deadline"
        icon={<Flag className="h-4 w-4" />}
        date={dueDate}
        quickOptions={dueQuickOptions}
        onSubmit={onDueDateChange}
        onClear={onDueDateClear}
        closeMenu={closeMenu}
      />
      <MoveTaskDestinationSub
        listId={task.listId}
        projectId={task.projectId}
        lists={lists}
        projectsByListId={projectsByListId}
        projectLookup={projectsLookup}
        onSelect={onMove}
        closeMenu={closeMenu}
      />
      {task.status === 'open' && (
        <ContextMenuItem
          onSelect={() => {
            onToggleSomeday();
            closeMenu();
          }}
        >
          <Clock className="h-4 w-4" />
          {task.isSomeday ? 'Move to Anytime' : 'Move to Someday'}
        </ContextMenuItem>
      )}
      <ContextMenuItem
        onSelect={() => {
          onOpenTags();
          closeMenu();
        }}
      >
        <Tag className="h-4 w-4" />
        Set tags...
      </ContextMenuItem>
      <RepeatRuleMenuSub
        repeatState={repeatState}
        menuOpen={menuOpen}
        onSubmit={onRepeatSubmit}
        onClear={onRepeatClear}
        closeMenu={closeMenu}
      />
      <ContextMenuSeparator />
      {task.status === 'open' ? (
        <>
          <ContextMenuItem
            onSelect={() => {
              onComplete();
              closeMenu();
            }}
          >
            <CheckCircle2 className="h-4 w-4" />
            Mark complete
          </ContextMenuItem>
          <ContextMenuItem
            onSelect={() => {
              onCancel();
              closeMenu();
            }}
          >
            <CircleX className="h-4 w-4" />
            Mark cancelled
          </ContextMenuItem>
        </>
      ) : (
        <ContextMenuItem
          onSelect={() => {
            onReopen();
            closeMenu();
          }}
        >
          <Circle className="h-4 w-4" />
          Reopen task
        </ContextMenuItem>
      )}
      <ContextMenuItem
        onSelect={() => {
          onDuplicate();
          closeMenu();
        }}
      >
        <Copy className="h-4 w-4" />
        Duplicate task
      </ContextMenuItem>
      <ContextMenuSeparator />
      <ContextMenuItem
        variant="destructive"
        onSelect={() => {
          onDelete();
          closeMenu();
        }}
      >
        <Trash2 className="h-4 w-4" />
        Delete task
      </ContextMenuItem>
    </ContextMenuContent>
  );
}

interface TaskDateMenuSubProps {
  label: string;
  icon: ReactNode;
  date: string | null;
  quickOptions: Array<{ label: string; getDate: () => Date }>;
  onSubmit: (value: string | null) => void;
  onClear: () => void;
  closeMenu: () => void;
}

function TaskDateMenuSub({ label, icon, date, quickOptions, onSubmit, onClear, closeMenu }: TaskDateMenuSubProps) {
  const selected = useMemo(() => parseDateOnly(date), [date]);

  return (
    <ContextMenuSub>
      <ContextMenuSubTrigger className="flex items-center gap-3">
        {icon}
        <div className="flex flex-col text-left">
          <span className="text-sm font-medium">{label}</span>
          <span className="text-xs text-muted-foreground">{date ? formatShortDate(date) : 'Not set'}</span>
        </div>
      </ContextMenuSubTrigger>
      <ContextMenuSubContent className="w-auto border-none bg-transparent p-0 shadow-none">
        <CalendarPopoverContent
          selected={selected ?? undefined}
          onSelect={(day) => {
            if (!day) {
              return;
            }
            onSubmit(formatDateOnly(day));
            closeMenu();
          }}
          quickOptions={quickOptions}
          onQuickSelect={(getDate) => {
            onSubmit(formatDateOnly(getDate()));
            closeMenu();
          }}
          onClear={() => {
            onClear();
            closeMenu();
          }}
          onClose={closeMenu}
        />
      </ContextMenuSubContent>
    </ContextMenuSub>
  );
}

interface MoveTaskDestinationSubProps {
  listId: string;
  projectId: string | null;
  lists: TodoList[];
  projectsByListId: Record<string, TodoProject[]>;
  projectLookup: Record<string, TodoProject>;
  onSelect: (destination: { listId: string; projectId: string | null }) => void;
  closeMenu: () => void;
}

function MoveTaskDestinationSub({
  listId,
  projectId,
  lists,
  projectsByListId,
  projectLookup,
  onSelect,
  closeMenu,
}: MoveTaskDestinationSubProps) {
  const currentList = lists.find((list) => list.id === listId) ?? null;
  const currentProject =
    projectId ? (projectsByListId[listId] ?? []).find((project) => project.id === projectId) ?? null : null;
  const fallbackProject = projectId && !currentProject ? projectLookup[projectId] ?? null : null;
  const destinationLabel = currentProject
    ? `${currentProject.name}${currentList ? ` · ${currentList.name}` : ''}`
    : fallbackProject
      ? `${fallbackProject.name}${currentList ? ` · ${currentList.name}` : ''}`
      : currentList?.name ?? 'Unknown list';

  return (
    <ContextMenuSub>
      <ContextMenuSubTrigger className="flex items-center gap-3">
        <ListTree className="h-4 w-4" />
        <div className="flex flex-col text-left">
          <span className="text-sm font-medium">Move</span>
          <span className="text-xs text-muted-foreground">{destinationLabel}</span>
        </div>
      </ContextMenuSubTrigger>
      <ContextMenuSubContent className="w-72 border-none bg-transparent p-0 shadow-none">
        <MoveDestinationMenu
          lists={lists}
          projectsByListId={projectsByListId}
          activeListId={listId}
          activeProjectId={projectId}
          onSelect={(target) => {
            onSelect(target);
            closeMenu();
          }}
        />
      </ContextMenuSubContent>
    </ContextMenuSub>
  );
}

interface MoveDestinationMenuProps {
  lists: TodoList[];
  projectsByListId: Record<string, TodoProject[]>;
  activeListId: string;
  activeProjectId: string | null;
  onSelect: (destination: { listId: string; projectId: string | null }) => void;
}

function MoveDestinationMenu({
  lists,
  projectsByListId,
  activeListId,
  activeProjectId,
  onSelect,
}: MoveDestinationMenuProps) {
  if (!lists.length) {
    return <div className="p-3 text-sm text-muted-foreground">No lists available</div>;
  }

  return (
    <div className="max-h-72 space-y-2 overflow-y-auto p-2">
      {lists.map((list) => {
        const listProjects = projectsByListId[list.id] ?? [];
        const isActiveList = activeListId === list.id && !activeProjectId;
        return (
          <div key={list.id} className="space-y-1">
            <button
              type="button"
              className={cn(
                'flex w-full items-center justify-between rounded-lg px-2 py-1.5 text-sm transition-colors',
                isActiveList ? 'bg-primary/10 text-primary' : 'hover:bg-muted'
              )}
              onClick={() => onSelect({ listId: list.id, projectId: null })}
            >
              <span className="truncate">{list.name}</span>
              {isActiveList ? <Check className="h-4 w-4" /> : null}
            </button>
            {listProjects.length > 0 ? (
              <div className="space-y-1 pl-5">
                {listProjects.map((project) => {
                  const isActiveProject = activeListId === list.id && activeProjectId === project.id;
                  return (
                    <button
                      key={project.id}
                      type="button"
                      className={cn(
                        'flex w-full items-center justify-between rounded-lg px-2 py-1.5 text-sm transition-colors text-muted-foreground',
                        isActiveProject ? 'bg-primary/10 text-primary' : 'hover:bg-muted'
                      )}
                      onClick={() => onSelect({ listId: list.id, projectId: project.id })}
                    >
                      <span className="truncate">{project.name}</span>
                      {isActiveProject ? <Check className="h-4 w-4" /> : null}
                    </button>
                  );
                })}
              </div>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

interface RepeatRuleMenuSubProps {
  repeatState: RepeatRuleState | null;
  menuOpen: boolean;
  onSubmit: (state: RepeatRuleState) => void;
  onClear: () => void;
  closeMenu: () => void;
}

function RepeatRuleMenuSub({ repeatState, menuOpen, onSubmit, onClear, closeMenu }: RepeatRuleMenuSubProps) {
  const [state, setState] = useState<RepeatRuleState>(() => hydrateRepeatState(repeatState));

  useEffect(() => {
    if (menuOpen) {
      setState(hydrateRepeatState(repeatState));
    }
  }, [menuOpen, repeatState]);

  const handleSave = () => {
    if (state.enabled) {
      onSubmit(state);
    } else {
      onClear();
    }
    closeMenu();
  };

  return (
    <ContextMenuSub>
      <ContextMenuSubTrigger className="flex items-center gap-3">
        <Repeat className="h-4 w-4" />
        <div className="flex flex-col text-left">
          <span className="text-sm font-medium">Repeat</span>
          <span className="text-xs text-muted-foreground">{formatRepeatDisplay(repeatState)}</span>
        </div>
      </ContextMenuSubTrigger>
      <ContextMenuSubContent className="w-[360px] max-w-[90vw] border-none bg-transparent p-0 shadow-none">
        <RepeatRuleForm
          state={state}
          onStateChange={setState}
          onClear={() => {
            onClear();
            closeMenu();
          }}
          onCancel={closeMenu}
          onSave={handleSave}
        />
      </ContextMenuSubContent>
    </ContextMenuSub>
  );
}

// Helper function to format dates in short format
function formatShortDate(dateString: string | null): string {
  if (!dateString) return '';
  const date = parseDateOnly(dateString);
  if (!date) return '';
  const today = new Date();

  const dateOnly = dateString.slice(0, 10);
  const todayOnly = formatDateOnly(today);

  if (dateOnly === todayOnly) return 'Today';

  // Return short format like "Tue", "Wed", "Dec 3"
  const dayOfWeek = date.toLocaleDateString(undefined, { weekday: 'short' });
  const month = date.toLocaleDateString(undefined, { month: 'short' });
  const day = date.getDate();

  // If within next 7 days, show day of week
  const diffTime = date.getTime() - today.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  if (diffDays >= 0 && diffDays <= 7) {
    return dayOfWeek;
  }

  return `${month} ${day}`;
}

// Helper function to format due dates with relative days
function formatDueDateDisplay(dateString: string | null): string {
  if (!dateString) return '';
  const date = parseDateOnly(dateString);
  if (!date) return '';

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  date.setHours(0, 0, 0, 0);

  const diffTime = date.getTime() - today.getTime();
  const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24));

  // Due today
  if (diffDays === 0) {
    return 'Today';
  }

  // Future dates within 60 days
  if (diffDays > 0 && diffDays <= 60) {
    return diffDays === 1 ? '1 day left' : `${diffDays} days left`;
  }

  // Overdue within 60 days
  if (diffDays < 0 && diffDays >= -60) {
    const daysAgo = Math.abs(diffDays);
    return daysAgo === 1 ? '1 day ago' : `${daysAgo} days ago`;
  }

  // Outside 60 day range - show the date
  return formatShortDate(dateString);
}

// Icon-only trigger components for empty fields
interface IconPopoverTriggerProps {
  icon: React.ReactNode;
  onOpen: () => void;
}

function IconPopoverTrigger({ icon, onOpen }: IconPopoverTriggerProps) {
  return (
    <button
      type="button"
      className="rounded-full p-1.5 transition-colors hover:bg-muted"
      onClick={(e) => {
        e.stopPropagation();
        onOpen();
      }}
    >
      {icon}
    </button>
  );
}

interface IconDateFieldTriggerProps {
  icon: React.ReactNode;
  label: string;
  date: string | null;
  onSubmit: (value: string | null) => void;
  onClear: () => void;
  quickOptions: Array<{ label: string; getDate: () => Date }>;
}

function IconDateFieldTrigger({ icon, label, date, onSubmit, onClear, quickOptions }: IconDateFieldTriggerProps) {
  const [open, setOpen] = useState(false);
  const selected = useMemo(() => parseDateOnly(date), [date]);

  const handleSelect = (day?: Date) => {
    if (day) {
      onSubmit(formatDateOnly(day));
      setOpen(false);
    }
  };

  const handleQuickSelect = (getDate: () => Date) => {
    const next = getDate();
    onSubmit(formatDateOnly(next));
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="rounded-full p-1.5 transition-colors hover:bg-muted"
          onClick={(e) => e.stopPropagation()}
          aria-label={label}
        >
          {icon}
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="w-auto p-0"
        onClick={(e) => e.stopPropagation()}
        onPointerDownOutside={(e) => {
          e.stopPropagation();
        }}
      >
        <CalendarPopoverContent
          selected={selected}
          onSelect={handleSelect}
          quickOptions={quickOptions}
          onQuickSelect={handleQuickSelect}
          onClear={() => { onClear(); setOpen(false); }}
          onClose={() => setOpen(false)}
        />
      </PopoverContent>
    </Popover>
  );
}

interface IconRepeatFieldTriggerProps {
  icon: React.ReactNode;
  label: string;
  repeatState: RepeatRuleState | null;
  onSubmit: (value: RepeatRuleState) => void;
  onClear: () => void;
}

function IconRepeatFieldTrigger({ icon, label, repeatState, onSubmit, onClear }: IconRepeatFieldTriggerProps) {
  return (
    <RepeatRulePopover repeatState={repeatState} onSubmit={onSubmit} onClear={onClear}>
      {() => (
        <button
          type="button"
          className="rounded-full p-1.5 transition-colors hover:bg-muted"
          onClick={(e) => e.stopPropagation()}
          aria-label={label}
        >
          {icon}
        </button>
      )}
    </RepeatRulePopover>
  );
}

// EditableDateField component - shows a date with icon that can be clicked to edit
interface EditableDateFieldProps {
  icon: React.ReactNode;
  label: string;
  date: string | null;
  onSubmit: (value: string | null) => void;
  onClear: () => void;
  quickOptions: Array<{ label: string; getDate: () => Date }>;
}

function EditableDateField({ icon, label, date, onSubmit, onClear, quickOptions }: EditableDateFieldProps) {
  const [open, setOpen] = useState(false);
  const [hovering, setHovering] = useState(false);
  const selected = useMemo(() => parseDateOnly(date), [date]);

  const handleSelect = (day?: Date) => {
    if (day) {
      onSubmit(formatDateOnly(day));
      setOpen(false);
    }
  };

  const handleQuickSelect = (getDate: () => Date) => {
    const next = getDate();
    onSubmit(formatDateOnly(next));
    setOpen(false);
  };

  const displayText = date ? formatLongDate(date) : `Add ${label.toLowerCase()}`;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="flex items-center gap-2 text-sm hover:text-foreground group"
          onClick={(e) => e.stopPropagation()}
          // Mouse only: on touch the hover-revealed clear button would make
          // iOS treat the first tap as a hover and drop the click.
          onPointerEnter={(e) => setHovering(e.pointerType === 'mouse')}
          onPointerLeave={() => setHovering(false)}
          aria-label={label}
        >
          {icon}
          <span className={cn(date ? 'text-foreground' : 'text-muted-foreground')}>
            {displayText}
          </span>
          {date && hovering && (
            <span
              role="button"
              tabIndex={0}
              aria-label="Clear date"
              className="ml-1 text-muted-foreground hover:text-foreground"
              onClick={(e) => {
                e.stopPropagation();
                onClear();
              }}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  event.stopPropagation();
                  onClear();
                }
              }}
            >
              ×
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="w-auto p-0"
        onClick={(e) => e.stopPropagation()}
        onPointerDownOutside={(e) => {
          e.stopPropagation();
        }}
      >
        <CalendarPopoverContent
          selected={selected}
          onSelect={handleSelect}
          quickOptions={quickOptions}
          onQuickSelect={handleQuickSelect}
          onClear={() => { onClear(); setOpen(false); }}
          onClose={() => setOpen(false)}
        />
      </PopoverContent>
    </Popover>
  );
}

// EditableRepeatField component - shows repeat settings with icon that can be clicked to edit
interface EditableRepeatFieldProps {
  icon: React.ReactNode;
  label: string;
  repeatState: RepeatRuleState | null;
  onSubmit: (value: RepeatRuleState) => void;
  onClear: () => void;
}

const weekdayOptions = [
  { label: 'Mon', value: 1 },
  { label: 'Tue', value: 2 },
  { label: 'Wed', value: 3 },
  { label: 'Thu', value: 4 },
  { label: 'Fri', value: 5 },
  { label: 'Sat', value: 6 },
  { label: 'Sun', value: 0 },
];

const weekOfMonthOptions = [
  { label: 'First', value: 1 },
  { label: 'Second', value: 2 },
  { label: 'Third', value: 3 },
  { label: 'Fourth', value: 4 },
];

const monthOptions = Array.from({ length: 12 }).map((_, index) => ({
  value: index + 1,
  label: new Date(2024, index, 1).toLocaleString(undefined, { month: 'long' }),
}));

const repeatTypes: Array<{ id: RepeatRuleState['rule']['ruleType']; label: string }> = [
  { id: 'daily', label: 'Daily' },
  { id: 'weekly', label: 'Weekly' },
  { id: 'monthly', label: 'Monthly' },
  { id: 'yearly', label: 'Yearly' },
  { id: 'after_completion', label: 'After Completion' },
];

const afterCompletionUnits = [
  { value: 'days', label: 'Days' },
  { value: 'weeks', label: 'Weeks' },
  { value: 'months', label: 'Months' },
  { value: 'years', label: 'Years' },
];

const defaultRepeatState: RepeatRuleState = {
  enabled: true,
  leadTimeDays: 0,
  rule: {
    ruleType: 'daily',
    intervalValue: 1,
    skipWeekends: false,
    daysOfWeek: [1],
    dayOfMonth: 1,
    weekOfMonth: 1,
    monthOfYear: 1,
    maxOccurrences: null,
    untilDate: null,
    metadata: { pattern: 'date', intervalUnit: 'days' },
  },
};

function hydrateRepeatState(initial: RepeatRuleState | null): RepeatRuleState {
  if (!initial) {
    return { ...defaultRepeatState, rule: { ...defaultRepeatState.rule, metadata: { ...defaultRepeatState.rule.metadata } } };
  }
  return {
    enabled: initial.enabled ?? true,
    leadTimeDays: initial.leadTimeDays ?? 0,
    rule: {
      ...defaultRepeatState.rule,
      ...initial.rule,
      metadata: { ...defaultRepeatState.rule.metadata, ...(initial.rule.metadata ?? {}) },
    },
  };
}

function clampDay(value: number) {
  if (Number.isNaN(value) || value < 1) {
    return 1;
  }
  if (value > 31) {
    return 31;
  }
  return value;
}

function formatRepeatDisplay(state: RepeatRuleState | null): string {
  if (!state || !state.enabled) {
    return 'Make repeating';
  }
  const { rule } = state;
  const intervalLabel = (() => {
    switch (rule.ruleType) {
      case 'daily':
        return rule.intervalValue === 1 ? 'day' : 'days';
      case 'weekly':
        return rule.intervalValue === 1 ? 'week' : 'weeks';
      case 'monthly':
        return rule.intervalValue === 1 ? 'month' : 'months';
      case 'yearly':
        return rule.intervalValue === 1 ? 'year' : 'years';
      case 'after_completion': {
        const unit = String(rule.metadata?.intervalUnit ?? 'days');
        return unit;
      }
      default:
        return 'days';
    }
  })();
  
  if (rule.ruleType === 'daily') {
    return `Every ${rule.intervalValue} ${intervalLabel}${rule.skipWeekends ? ' (weekdays)' : ''}`;
  }
  if (rule.ruleType === 'weekly') {
    return `Every ${rule.intervalValue} ${intervalLabel}`;
  }
  if (rule.ruleType === 'monthly') {
    return `Every ${rule.intervalValue} ${intervalLabel}`;
  }
  if (rule.ruleType === 'yearly') {
    return `Every ${rule.intervalValue} ${intervalLabel}`;
  }
  if (rule.ruleType === 'after_completion') {
    return `After completion, every ${rule.intervalValue} ${intervalLabel}`;
  }
  return 'Repeating';
}

interface RepeatRulePopoverProps {
  repeatState: RepeatRuleState | null;
  onSubmit: (value: RepeatRuleState) => void;
  onClear: () => void;
  children: (props: { open: boolean; setOpen: (open: boolean) => void }) => React.ReactElement;
}

function RepeatRulePopover({ repeatState, onSubmit, onClear, children }: RepeatRulePopoverProps) {
  const [open, setOpen] = useState(false);
  const [state, setState] = useState<RepeatRuleState>(() => hydrateRepeatState(repeatState));

  useEffect(() => {
    if (open) {
      setState(hydrateRepeatState(repeatState));
    }
  }, [repeatState, open]);

  const handleSave = () => {
    if (state.enabled) {
      onSubmit(state);
    } else {
      onClear();
    }
    setOpen(false);
  };

  // Detect mobile for centered positioning
  const isMobile = useIsMobile();

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{children({ open, setOpen })}</PopoverTrigger>
      <PopoverContent
        className="p-0 rounded-3xl border-border/70 bg-popover shadow-lg max-h-[80vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        align="center"
        side="bottom"
        sideOffset={8}
        style={isMobile ? {
          position: 'fixed',
          inset: 0,
          margin: 'auto',
          width: 'calc(100vw - 32px)',
          maxWidth: '400px',
          height: 'fit-content',
        } : { width: 'auto', maxWidth: '400px' }}
      >
        <RepeatRuleForm
          state={state}
          onStateChange={setState}
          onClear={() => {
            onClear();
            setOpen(false);
          }}
          onCancel={() => setOpen(false)}
          onSave={handleSave}
        />
      </PopoverContent>
    </Popover>
  );
}

interface RepeatRuleFormProps {
  state: RepeatRuleState;
  onStateChange: Dispatch<SetStateAction<RepeatRuleState>>;
  onClear: () => void;
  onCancel: () => void;
  onSave: () => void;
}

function RepeatRuleForm({ state, onStateChange, onClear, onCancel, onSave }: RepeatRuleFormProps) {
  const intervalLabel = useMemo(() => {
    switch (state.rule.ruleType) {
      case 'daily':
        return state.rule.intervalValue === 1 ? 'day' : 'days';
      case 'weekly':
        return state.rule.intervalValue === 1 ? 'week' : 'weeks';
      case 'monthly':
        return state.rule.intervalValue === 1 ? 'month' : 'months';
      case 'yearly':
        return state.rule.intervalValue === 1 ? 'year' : 'years';
      case 'after_completion': {
        const unit = String(state.rule.metadata?.intervalUnit ?? 'days');
        return unit;
      }
      default:
        return 'days';
    }
  }, [state.rule.intervalValue, state.rule.metadata?.intervalUnit, state.rule.ruleType]);

  const endMode = state.rule.maxOccurrences
    ? 'count'
    : state.rule.untilDate
      ? 'date'
      : 'never';

  const isWeekdayPattern = state.rule.metadata?.pattern === 'weekday';

  const updateRule = (updates: Partial<RepeatRuleState['rule']>) => {
    onStateChange((prev) => ({
      ...prev,
      rule: {
        ...prev.rule,
        ...updates,
      },
    }));
  };

  const updateMetadata = (updates: Record<string, unknown>) => {
    onStateChange((prev) => ({
      ...prev,
      rule: {
        ...prev.rule,
        metadata: { ...(prev.rule.metadata ?? {}), ...updates },
      },
    }));
  };

  return (
    <div className="p-4 space-y-4">
      <div className="flex items-center justify-between rounded-2xl border border-border/60 px-3 py-2">
        <div>
          <p className="text-sm font-medium">Enable repetition</p>
          <p className="text-xs text-muted-foreground">When off, this task stops repeating.</p>
        </div>
        <Switch checked={state.enabled} onCheckedChange={(checked) => onStateChange((prev) => ({ ...prev, enabled: checked }))} />
      </div>
      <div className="space-y-2">
        <Label>Frequency</Label>
        <div className="grid grid-cols-2 gap-2 md:grid-cols-3">
          {repeatTypes.map((type) => (
            <button
              key={type.id}
              type="button"
              className={cn(
                'rounded-2xl border px-3 py-2 text-sm font-medium transition-colors',
                state.rule.ruleType === type.id ? 'border-primary bg-primary/10 text-primary' : 'border-border/60 hover:border-border'
              )}
              onClick={() => updateRule({ ruleType: type.id })}
            >
              {type.label}
            </button>
          ))}
        </div>
      </div>
      <div className="grid gap-3 md:grid-cols-[180px,1fr]">
        <div>
          <Label>Every</Label>
          <Input
            type="number"
            min={1}
            value={state.rule.intervalValue}
            onChange={(event) => updateRule({ intervalValue: Math.max(1, Number(event.target.value) || 1) })}
          />
        </div>
        <div className="flex items-end">
          <p className="text-sm text-muted-foreground">
            {state.rule.ruleType === 'after_completion' ? 'After completion repeat every' : 'Repeat every'}{' '}
            <span className="font-semibold">{intervalLabel}</span>
          </p>
        </div>
      </div>
      {state.rule.ruleType === 'daily' && (
        <div className="flex items-center justify-between rounded-2xl border border-border/60 px-3 py-2">
          <div>
            <p className="text-sm font-medium">Skip weekends</p>
            <p className="text-xs text-muted-foreground">Don't schedule repeats on Saturdays or Sundays.</p>
          </div>
          <Switch
            checked={Boolean(state.rule.skipWeekends)}
            onCheckedChange={(checked) => updateRule({ skipWeekends: checked })}
          />
        </div>
      )}
      {state.rule.ruleType === 'weekly' && (
        <div className="space-y-2">
          <Label>Days of the week</Label>
          <div className="flex flex-wrap gap-2">
            {weekdayOptions.map((day) => {
              const active = state.rule.daysOfWeek?.includes(day.value) ?? false;
              return (
                <button
                  key={day.value}
                  type="button"
                  className={cn(
                    'rounded-full px-3 py-1 text-xs font-semibold transition-colors',
                    active ? 'bg-primary text-primary-foreground' : 'bg-muted text-foreground'
                  )}
                  onClick={() => {
                    const next = new Set(state.rule.daysOfWeek ?? []);
                    if (next.has(day.value)) {
                      next.delete(day.value);
                    } else {
                      next.add(day.value);
                    }
                    updateRule({ daysOfWeek: Array.from(next).sort() });
                  }}
                >
                  {day.label}
                </button>
              );
            })}
          </div>
        </div>
      )}
      {state.rule.ruleType === 'monthly' && (
        <div className="space-y-3 rounded-2xl border border-border/60 p-3">
          <div className="flex gap-2">
            <button
              type="button"
              className={cn(
                'flex-1 rounded-xl border px-3 py-2 text-sm font-medium',
                !isWeekdayPattern ? 'border-primary bg-primary/10 text-primary' : 'border-border/60'
              )}
              onClick={() => updateMetadata({ pattern: 'date' })}
            >
              On day #{state.rule.dayOfMonth}
            </button>
            <button
              type="button"
              className={cn(
                'flex-1 rounded-xl border px-3 py-2 text-sm font-medium',
                isWeekdayPattern ? 'border-primary bg-primary/10 text-primary' : 'border-border/60'
              )}
              onClick={() => updateMetadata({ pattern: 'weekday' })}
            >
              On weekday
            </button>
          </div>
          {!isWeekdayPattern ? (
            <div>
              <Label>Day of month</Label>
              <Input
                type="number"
                min={1}
                max={31}
                value={state.rule.dayOfMonth ?? 1}
                onChange={(event) => updateRule({ dayOfMonth: clampDay(Number(event.target.value)) })}
              />
            </div>
          ) : (
            <div className="grid gap-3 md:grid-cols-2">
              <div>
                <Label>Week</Label>
                <Select
                  value={String(state.rule.weekOfMonth ?? 1)}
                  onValueChange={(value) => updateRule({ weekOfMonth: Number(value) })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {weekOfMonthOptions.map((option) => (
                      <SelectItem key={option.value} value={String(option.value)}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Weekday</Label>
                <Select
                  value={String(state.rule.daysOfWeek?.[0] ?? 1)}
                  onValueChange={(value) => updateRule({ daysOfWeek: [Number(value)] })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {weekdayOptions.map((option) => (
                      <SelectItem key={option.value} value={String(option.value)}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
        </div>
      )}
      {state.rule.ruleType === 'yearly' && (
        <div className="space-y-3 rounded-2xl border border-border/60 p-3">
          <div>
            <Label>Month</Label>
            <Select
              value={String(state.rule.monthOfYear ?? 1)}
              onValueChange={(value) => updateRule({ monthOfYear: Number(value) })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {monthOptions.map((month) => (
                  <SelectItem key={month.value} value={String(month.value)}>
                    {month.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Day of month</Label>
            <Input
              type="number"
              min={1}
              max={31}
              value={state.rule.dayOfMonth ?? 1}
              onChange={(event) => updateRule({ dayOfMonth: clampDay(Number(event.target.value)) })}
            />
          </div>
        </div>
      )}
      {state.rule.ruleType === 'after_completion' && (
        <div className="space-y-2">
          <Label>Interval unit</Label>
          <div className="grid grid-cols-2 gap-2">
            {afterCompletionUnits.map((unit) => (
              <button
                key={unit.value}
                type="button"
                className={cn(
                  'rounded-xl border px-3 py-2 text-sm font-medium',
                  state.rule.metadata?.intervalUnit === unit.value ? 'border-primary bg-primary/10 text-primary' : 'border-border/60'
                )}
                onClick={() => updateMetadata({ intervalUnit: unit.value })}
              >
                {unit.label}
              </button>
            ))}
          </div>
        </div>
      )}
      <div className="grid gap-3 md:grid-cols-2">
        <div>
          <Label>Lead time (days)</Label>
          <Input
            type="number"
            min={0}
            value={state.leadTimeDays ?? 0}
            onChange={(event) => onStateChange((prev) => ({ ...prev, leadTimeDays: Math.max(0, Number(event.target.value) || 0) }))}
          />
        </div>
        <div>
          <Label>Ends</Label>
          <div className="flex flex-wrap gap-2 pt-2 text-sm">
            {['never', 'count', 'date'].map((mode) => (
              <button
                key={mode}
                type="button"
                className={cn(
                  'flex-1 rounded-xl border px-3 py-1.5 capitalize',
                  endMode === mode ? 'border-primary bg-primary/10 text-primary' : 'border-border/60'
                )}
                onClick={() => {
                  if (mode === 'never') {
                    updateRule({ maxOccurrences: null, untilDate: null });
                  } else if (mode === 'count') {
                    updateRule({ maxOccurrences: state.rule.maxOccurrences ?? 5, untilDate: null });
                  } else {
                    updateRule({ untilDate: state.rule.untilDate ?? formatDateOnly(new Date()), maxOccurrences: null });
                  }
                }}
              >
                {mode === 'never' ? 'Never' : mode === 'count' ? 'After # times' : 'On date'}
              </button>
            ))}
          </div>
        </div>
      </div>
      {endMode === 'count' && (
        <div>
          <Label>Occurrences</Label>
          <Input
            type="number"
            min={1}
            value={state.rule.maxOccurrences ?? 1}
            onChange={(event) => updateRule({ maxOccurrences: Math.max(1, Number(event.target.value) || 1) })}
          />
        </div>
      )}
      {endMode === 'date' && (
        <div>
          <Label>End date</Label>
          <Input
            type="date"
            value={state.rule.untilDate ?? ''}
            onChange={(event) => updateRule({ untilDate: event.target.value || null })}
          />
        </div>
      )}
      <div className="flex items-center justify-between border-t pt-2">
        <Button variant="ghost" size="sm" onClick={onClear}>
          Clear
        </Button>
        <div className="flex gap-2">
          <Button variant="ghost" size="sm" onClick={onCancel}>
            Cancel
          </Button>
          <Button size="sm" onClick={onSave}>
            Save
          </Button>
        </div>
      </div>
    </div>
  );
}

function EditableRepeatField({ icon, label, repeatState, onSubmit, onClear }: EditableRepeatFieldProps) {
  const [hovering, setHovering] = useState(false);
  const displayText = formatRepeatDisplay(repeatState);

  return (
    <RepeatRulePopover repeatState={repeatState} onSubmit={onSubmit} onClear={onClear}>
      {() => (
        <button
          type="button"
          className="flex items-center gap-2 text-sm hover:text-foreground group"
          onClick={(e) => e.stopPropagation()}
          // Mouse only: on touch the hover-revealed clear button would make
          // iOS treat the first tap as a hover and drop the click.
          onPointerEnter={(e) => setHovering(e.pointerType === 'mouse')}
          onPointerLeave={() => setHovering(false)}
          aria-label={label}
        >
          {icon}
          <span className={cn(repeatState?.enabled ? 'text-foreground' : 'text-muted-foreground')}>{displayText}</span>
          {repeatState?.enabled && hovering && (
            <span
              role="button"
              tabIndex={0}
              aria-label="Clear repeat rule"
              className="ml-1 text-muted-foreground hover:text-foreground"
              onClick={(e) => {
                e.stopPropagation();
                onClear();
              }}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  event.stopPropagation();
                  onClear();
                }
              }}
            >
              ×
            </span>
          )}
        </button>
      )}
    </RepeatRulePopover>
  );
}

// Format date in long format for expanded view
function formatLongDate(dateString: string | null): string {
  if (!dateString) return '';
  const date = parseDateOnly(dateString);
  if (!date) return '';
  return date.toLocaleDateString(undefined, { weekday: 'long', month: 'short', day: 'numeric' });
}

// Swipe-triggered context menu content (uses buttons instead of ContextMenuItems)
interface SwipeContextMenuContentProps {
  task: TodoTask;
  schedule: string | null;
  dueDate: string | null;
  repeatState: RepeatRuleState | null;
  lists: TodoList[];
  projectsByListId: Record<string, TodoProject[]>;
  projectsLookup: Record<string, TodoProject>;
  onScheduleChange: (value: string | null) => void;
  onScheduleClear: () => void;
  onDueDateChange: (value: string | null) => void;
  onDueDateClear: () => void;
  onMove: (destination: { listId: string; projectId: string | null }) => void;
  onOpenTags: () => void;
  onRepeatSubmit: (state: RepeatRuleState) => void;
  onRepeatClear: () => void;
  onDuplicate: () => void;
  onComplete: () => void;
  onCancel: () => void;
  onReopen: () => void;
  onDelete: () => void;
  onToggleSomeday: () => void;
  closeMenu: () => void;
}

function SwipeContextMenuContent({
  task,
  schedule,
  dueDate,
  repeatState,
  lists,
  projectsByListId,
  projectsLookup,
  onScheduleChange,
  onScheduleClear,
  onDueDateChange,
  onDueDateClear,
  onMove,
  onOpenTags,
  onRepeatSubmit,
  onRepeatClear,
  onDuplicate,
  onComplete,
  onCancel,
  onReopen,
  onDelete,
  onToggleSomeday,
  closeMenu,
}: SwipeContextMenuContentProps) {
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [deadlineOpen, setDeadlineOpen] = useState(false);
  const [moveOpen, setMoveOpen] = useState(false);

  const menuItemClass = 'flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm transition-colors hover:bg-muted text-left';
  const destructiveClass = 'flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm transition-colors hover:bg-destructive/10 text-destructive text-left';

  const currentList = lists.find((list) => list.id === task.listId) ?? null;
  const currentProject = task.projectId
    ? (projectsByListId[task.listId] ?? []).find((project) => project.id === task.projectId) ?? projectsLookup[task.projectId] ?? null
    : null;
  const destinationLabel = currentProject
    ? `${currentProject.name}${currentList ? ` · ${currentList.name}` : ''}`
    : currentList?.name ?? 'Unknown list';

  return (
    <div className="space-y-1">
      <div className="px-2 py-1.5 text-sm font-semibold line-clamp-2">
        {task.title || 'Untitled Task'}
      </div>

      {/* Schedule */}
      <Popover open={scheduleOpen} onOpenChange={setScheduleOpen}>
        <PopoverTrigger asChild>
          <button type="button" className={menuItemClass}>
            <CalendarDays className="h-4 w-4" />
            <div className="flex flex-col text-left">
              <span>Schedule</span>
              <span className="text-xs text-muted-foreground">{schedule ? formatShortDate(schedule) : 'Not set'}</span>
            </div>
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0 border-none bg-transparent shadow-none" align="start">
          <CalendarPopoverContent
            selected={parseDateOnly(schedule) ?? undefined}
            onSelect={(day) => {
              if (day) {
                onScheduleChange(formatDateOnly(day));
                setScheduleOpen(false);
              }
            }}
            quickOptions={scheduleQuickOptions}
            onQuickSelect={(getDate) => {
              onScheduleChange(formatDateOnly(getDate()));
              setScheduleOpen(false);
            }}
            onClear={() => {
              onScheduleClear();
              setScheduleOpen(false);
            }}
            onClose={() => setScheduleOpen(false)}
          />
        </PopoverContent>
      </Popover>

      {/* Deadline */}
      <Popover open={deadlineOpen} onOpenChange={setDeadlineOpen}>
        <PopoverTrigger asChild>
          <button type="button" className={menuItemClass}>
            <Flag className="h-4 w-4" />
            <div className="flex flex-col text-left">
              <span>Deadline</span>
              <span className="text-xs text-muted-foreground">{dueDate ? formatShortDate(dueDate) : 'Not set'}</span>
            </div>
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0 border-none bg-transparent shadow-none" align="start">
          <CalendarPopoverContent
            selected={parseDateOnly(dueDate) ?? undefined}
            onSelect={(day) => {
              if (day) {
                onDueDateChange(formatDateOnly(day));
                setDeadlineOpen(false);
              }
            }}
            quickOptions={dueQuickOptions}
            onQuickSelect={(getDate) => {
              onDueDateChange(formatDateOnly(getDate()));
              setDeadlineOpen(false);
            }}
            onClear={() => {
              onDueDateClear();
              setDeadlineOpen(false);
            }}
            onClose={() => setDeadlineOpen(false)}
          />
        </PopoverContent>
      </Popover>

      {/* Move */}
      <Popover open={moveOpen} onOpenChange={setMoveOpen}>
        <PopoverTrigger asChild>
          <button type="button" className={menuItemClass}>
            <ListTree className="h-4 w-4" />
            <div className="flex flex-col text-left">
              <span>Move</span>
              <span className="text-xs text-muted-foreground">{destinationLabel}</span>
            </div>
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-72 p-0" align="start">
          <MoveDestinationMenu
            lists={lists}
            projectsByListId={projectsByListId}
            activeListId={task.listId}
            activeProjectId={task.projectId}
            onSelect={(target) => {
              onMove(target);
              setMoveOpen(false);
            }}
          />
        </PopoverContent>
      </Popover>

      {/* Someday toggle */}
      {task.status === 'open' && (
        <button
          type="button"
          className={menuItemClass}
          onClick={() => {
            onToggleSomeday();
            closeMenu();
          }}
        >
          <Clock className="h-4 w-4" />
          {task.isSomeday ? 'Move to Anytime' : 'Move to Someday'}
        </button>
      )}

      {/* Tags */}
      <button
        type="button"
        className={menuItemClass}
        onClick={() => {
          onOpenTags();
          closeMenu();
        }}
      >
        <Tag className="h-4 w-4" />
        Set tags...
      </button>

      {/* Repeat */}
      <RepeatRulePopover repeatState={repeatState} onSubmit={onRepeatSubmit} onClear={onRepeatClear}>
        {({ open: repeatOpen }) => (
          <button type="button" className={cn(menuItemClass, repeatOpen && 'bg-muted')}>
            <Repeat className="h-4 w-4" />
            <div className="flex flex-col text-left">
              <span>Repeat</span>
              <span className="text-xs text-muted-foreground">{formatRepeatDisplay(repeatState)}</span>
            </div>
          </button>
        )}
      </RepeatRulePopover>

      <div className="my-1 h-px bg-border" />

      {task.status === 'open' ? (
        <>
          <button
            type="button"
            className={menuItemClass}
            onClick={() => {
              onComplete();
              closeMenu();
            }}
          >
            <CheckCircle2 className="h-4 w-4" />
            Mark complete
          </button>
          <button
            type="button"
            className={menuItemClass}
            onClick={() => {
              onCancel();
              closeMenu();
            }}
          >
            <CircleX className="h-4 w-4" />
            Mark cancelled
          </button>
        </>
      ) : (
        <button
          type="button"
          className={menuItemClass}
          onClick={() => {
            onReopen();
            closeMenu();
          }}
        >
          <Circle className="h-4 w-4" />
          Reopen task
        </button>
      )}

      <button
        type="button"
        className={menuItemClass}
        onClick={() => {
          onDuplicate();
          closeMenu();
        }}
      >
        <Copy className="h-4 w-4" />
        Duplicate task
      </button>

      <div className="my-1 h-px bg-border" />

      <button
        type="button"
        className={destructiveClass}
        onClick={() => {
          onDelete();
          closeMenu();
        }}
      >
        <Trash2 className="h-4 w-4" />
        Delete task
      </button>
    </div>
  );
}
